/**
 * Textos de `onboarding` en español — la fuente. `en/onboarding.ts` tiene que tener
 * exactamente las mismas claves (lo exige el tipo), así que una clave nueva
 * acá rompe el typecheck hasta que alguien la traduce.
 *
 * Interpolación con funciones, no con plantillas "{n}": el compilador revisa
 * los argumentos y cada idioma arma su propia gramática (plurales, orden).
 */
/** "2 datos" / "1 dato". */
function datos(gaps: number): string {
  return `${gaps} ${gaps === 1 ? 'dato' : 'datos'}`;
}

/** "1 punto" / "6 puntos". */
function puntos(n: number): string {
  return `${n} ${n === 1 ? 'punto' : 'puntos'}`;
}

const onboarding = {
  /** La portada (`JobApplyLandingPage`). */
  landing: {
    title: 'Deja que la IA postule por ti',
    lede: 'Conecta tu Gmail, sube tu hoja de vida y define tus preferencias. Enviaremos postulaciones personalizadas desde tu correo, todos los dias.',
    cta: 'Comenzar',
    /** La línea de la app debajo del CTA. */
    app: 'Descarga la app:',
    playSoon: 'Google Play muy pronto',
    /** El enlace discreto a la landing del Programa de socios. */
    partners: '¿Recomiendas TBit Jobs? Conoce el Programa de socios',
  },

  /** Errores que llegan en `?error=` al volver del OAuth de Gmail. */
  errors: {
    missing_gmail_scope: 'No se otorgaron los permisos de Gmail necesarios. Intenta de nuevo y acepta todos los permisos.',
    auth_failed: 'Hubo un error durante la autenticación. Intenta de nuevo.',
    no_code: 'No se recibió el código de autorización. Intenta de nuevo.',
    not_authenticated: 'Debes iniciar sesión primero.',
    gmail_signup_disabled: 'Por ahora las cuentas nuevas usan el correo que administra TBit.',
    /** Un código que no conocemos: se muestra tal cual. */
    unknown: (code: string) => `Error: ${code}`,
  },

  /** Errores de `api/onboardingClient.ts` (activar el canal gestionado). */
  client: {
    domain_channel_disabled: 'Estamos habilitando los envíos. Vuelve a intentar en un momento.',
    unauthorized: 'Tu sesión se venció. Vuelve a entrar.',
    network: 'Sin conexión. Revisa tu internet e intenta de nuevo.',
    unknown: 'No pudimos activar tu cuenta. Intenta de nuevo.',
  },

  /** `ConsentStep`: términos + perfil público, al entrar (y una vez a las cuentas que ya existen). */
  consent: {
    step: 'Antes de entrar',
    title: '¿Quién puede ver tu perfil?',
    lede: 'Con tu perfil público, los empleadores pueden encontrarte en Google y en TBit Jobs sin que te postules uno por uno.',
    groupLabel: 'Quién puede ver tu perfil',
    publicLabel: 'Público',
    recommended: 'Recomendado',
    publicHint: 'Tu perfil tiene su propia página en TBit Jobs y puede aparecer en Google.',
    privateLabel: 'Privado',
    privateHint: 'Solo te ven las agencias de la bolsa de talento, sin tus datos de contacto.',
    seenTitle: 'Lo que se ve',
    seenBody: 'Tu nombre y apellido, tu oficio, tus años de experiencia, tus habilidades y tu país.',
    neverTitle: 'Lo que nunca se ve',
    neverBody: 'Tu correo, tu teléfono, tu hoja de vida en PDF, tu puntaje y tu situación migratoria.',
    ack: {
      before: 'Entiendo que mi perfil va a ser ',
      strong: 'público',
      after: ' y que Google podrá mostrarlo. Puedo volverlo privado cuando quiera en Perfil → Quién te encuentra.',
      required: 'Marca que entiendes que tu perfil va a ser público, o elige Privado.',
    },
    terms: {
      before: 'Acepto los ',
      label: 'Términos y condiciones de TBit Jobs',
      href: 'https://tbit.app/es/legal/tbit-jobs-terminos-y-condiciones',
      after: '.',
      required: 'Marca la casilla para aceptar los términos y continuar.',
      outdated: 'Actualizamos los términos. Léelos y vuelve a marcar la casilla.',
    },
    cta: 'Ir a mi panel',
    saving: 'Guardando…',
    noCard: 'No te pedimos tarjeta.',
    close: 'Cerrar',
    closeNote: 'Si cierras esto, tu perfil queda privado hasta que decidas.',
    saveFailed: 'No pudimos guardar tu decisión. Inténtalo de nuevo.',
    connectionError: 'Error de conexión. Intenta de nuevo.',
  },

  /** Alta completa. */
  done: {
    title: '¡Todo listo!',
    body: 'Tu cuenta está configurada.',
    cta: 'Ir al panel',
  },

  /** Paso 1: el correo desde el que sales. */
  sender: {
    title: 'Empieza a postular hoy',
    lede: 'Nosotros ponemos el correo desde el que salen tus postulaciones y te reenviamos cada respuesta a tu correo de siempre.',
    pointSend: 'Enviamos una postulación personalizada por cada oferta',
    pointReplies: 'Cada respuesta de una empresa llega a tu correo',
    pointNoSpam: 'Nunca enviamos spam ni correos no autorizados',
    activationFailed: 'No pudimos activar tu cuenta. Intenta de nuevo.',
    ctaManaged: 'Continuar con envío gestionado por TBit',
    ctaStart: 'Empezar',
    ctaHint: 'Sin conectar ninguna cuenta. Tardas menos de un minuto.',
    or: 'o',
    ctaGmail: 'Prefiero postular desde mi Gmail',
    moreInfo: 'Más información',
    managedTitle: 'Envío gestionado por TBit:',
    managedBody: 'tus postulaciones salen de una dirección a tu nombre que administramos nosotros, y todas las respuestas se reenvían a tu correo. No pedimos ninguna contraseña.',
    gmailTitle: 'Desde tu Gmail:',
    gmailBody: 'solicitamos permisos de lectura y envío de correo para poder (1) enviar postulaciones desde tu cuenta de Gmail y (2) detectar cuándo una empresa responde a tu postulación. Tus credenciales están encriptadas y nunca se comparten con terceros. Puedes revocar el acceso en cualquier momento desde tu cuenta de Google.',
  },

  /** Paso 2: la hoja de vida, por dos puertas. */
  cvGate: {
    step: 'Paso 2 de 2',
    title: 'Tu hoja de vida',
    lede: 'Es lo que mandamos a cada empresa. Elige cómo quieres tenerla.',
    uploadTitle: 'Sube tu hoja de vida',
    uploadLede: 'Si ya la tienes en PDF o Word, la leemos y sacamos tu experiencia.',
    uploadBullets: ['Toma menos de un minuto', 'PDF o DOCX, máximo 5 MB'],
    uploadCta: 'Subir archivo',
    buildTitle: 'Crea tu hoja de vida con nosotros',
    buildLede: 'Contestas unas preguntas en español y nosotros la escribimos por ti.',
    buildBullets: [
      'Sale en inglés, que es como la piden allá',
      'Con el formato que leen los empleadores del DOL',
    ],
    buildCta: 'Empezar las preguntas',
  },

  /** Subir el archivo: la puerta 1 del alta y la pantalla suelta de hoja de vida. */
  upload: {
    step: 'Paso 2 de 2',
    title: 'Sube tu hoja de vida',
    lede: 'Extraeremos tus habilidades y experiencia para personalizar las postulaciones.',
    /** La pantalla suelta (`JobApplyCVPage`) nombra los formatos en la frase. */
    ledeStandalone: 'Sube un archivo PDF o DOCX. Extraeremos tus habilidades y experiencia para personalizar las postulaciones.',
    invalidType: 'Ese archivo no es PDF ni Word (DOCX). Elige tu hoja de vida en uno de esos dos formatos.',
    tooLarge: 'El archivo pesa más de 5 MB. Guárdalo otra vez como PDF o comprímelo, y vuelve a elegirlo.',
    /**
     * El teléfono no deja abrir el archivo elegido (api/cvUploadClient.ts):
     * típico de Drive o de los documentos de WhatsApp en Android.
     */
    unreadable:
      'No pudimos abrir este archivo en tu teléfono. Pasa mucho con archivos de Drive o de WhatsApp: descárgalo primero (que quede en Descargas) y elígelo otra vez.',
    /** La subida no llegó al servidor, ni al reintentar. */
    network:
      'No pudimos enviar tu hoja de vida porque se cortó la conexión. Revisa tu internet (con wifi suele funcionar mejor) e intenta de nuevo.',
    /** Sólo en el alta, donde está la otra puerta. Recibe el texto del botón. */
    questionnaireHint: (button: string) =>
      `Si sigue fallando, toca «${button}» y la armamos contigo con unas preguntas.`,
    dropFile: 'Arrastra tu archivo aqui',
    dropHint: 'PDF o DOCX, max 5 MB',
    dropCv: 'Arrastra tu hoja de vida aqui',
    dropCvHint: 'o haz clic para buscar (PDF, DOCX — max 5 MB)',
    httpError: (status: number) =>
      `No pudimos procesar tu hoja de vida (error ${status}). Intenta de nuevo en unos minutos.`,
    failed: 'No pudimos subir el archivo. Intenta de nuevo.',
    submit: 'Subir hoja de vida',
    notAtHand: 'No la tengo a mano',
    uploaded: 'Hoja de vida subida correctamente.',
    continue: 'Continuar',
    processed: 'Hoja de vida procesada correctamente',
    skillsTitle: 'Habilidades detectadas',
    noSkills: 'No se detectaron habilidades.',
    /** Recibe el número ya formateado en el idioma activo. */
    extracted: (chars: string) => `Se extrajeron ${chars} caracteres del documento.`,
  },

  /**
   * El WhatsApp del candidato: en el alta (las dos puertas) y en Perfil. Se
   * guarda en la membresía (`whatsapp_phone_e164`) y es el número por el que le
   * escribimos y con el que llama a la evaluación de inglés.
   */
  whatsapp: {
    label: 'Tu WhatsApp',
    optional: ' (opcional)',
    consent: 'Te escribimos por WhatsApp sobre tus postulaciones y si un empleador quiere evaluar tu inglés.',
    country: 'País',
    placeholder: '300 123 4567',
    invalid: 'Revisa el número: elige tu país y escríbelo sin el indicativo.',
    /** El alta siguió: el número se puede agregar después. */
    saveFailedAfterCv: 'Guardamos tu hoja de vida, pero no tu WhatsApp. Puedes agregarlo en Perfil.',
    /** La tarjeta de Perfil. */
    empty: 'Todavía no nos has dado tu WhatsApp.',
    edit: 'Cambiar',
    add: 'Agregar mi WhatsApp',
    save: 'Guardar',
    cancel: 'Cancelar',
    saved: 'Listo, guardamos tu WhatsApp.',
    failed: 'No pudimos guardar tu WhatsApp. Intenta de nuevo.',
  },

  /** `JobApplyProfileLayout`: las tres tabs del perfil. */
  profile: {
    title: 'Tu perfil',
    navLabel: 'Secciones del perfil',
    /** Botón del encabezado de Perfil: abre la hoja pública (jobs.tbit.app/perfiles/…). */
    viewPublicProfile: 'Ver mi perfil público',
    /** Con el perfil privado: lleva a «Quién te encuentra» para activarlo. */
    enablePublicProfile: 'Activar mi perfil público',
    /** El nombre de la tab de la bolsa con el interruptor de free tier apagado (el de antes). */
    talentPoolTabLegacy: 'Bolsa de talento',
    tabs: {
      cv: 'Hoja de vida',
      preferences: 'Preferencias',
      talentPool: 'Quién te encuentra',
      plan: 'Plan',
      partners: 'Programa de socios',
      english: 'Inglés',
    },
    ledes: {
      cv: 'De acá sale todo: con este documento escribimos cada postulación y armamos el resumen que ven las agencias.',
      preferences:
        'Cuéntanos qué buscas. Las agencias que publican vacantes con nosotros usan esto para saber si una oferta te sirve antes de escribirte.',
      talentPool: 'Decide si las agencias pueden encontrarte sin que tengas que postularte una por una.',
      plan: 'Tu plan es lo que hace que postulemos por ti todos los días hábiles. Acá ves hasta cuándo va.',
      partners: 'Recomienda TBit Jobs con tu código y gana por cada persona que se suscriba.',
      english: 'Tus evaluaciones de inglés: las que te piden las empresas y tus resultados.',
    },
  },

  /** `JobApplyCVPage`: la nota arriba y el cargador para reemplazar el archivo. */
  cvPage: {
    scoreUnavailable: 'No alcanzamos a calificarla ahora mismo. Vuelve a intentarlo en un momento.',
    scoring: 'Revisando tu hoja de vida',
    retry: 'Reintentar',
    scope: 'Qué tan fuerte está',
    addMissing: 'Agregar estos datos',
    reviewAnswers: 'Repasar mis respuestas',
    orUploadBelow: 'O sube una versión nueva del archivo aquí abajo.',
    uploadNew: 'Sube una versión nueva',
  },

  /** El puntaje de la hoja de vida: `lib/cvScore.ts`, `CVScoreCard`, `api/cvScoreClient.ts`. */
  cvScore: {
    /** El rótulo de la tarjeta cuando quien la usa no pone otro. */
    defaultScope: 'Tu hoja de vida',
    gauge: (score: number) => `Puntaje ${score} de 100`,
    gapsTitle: 'Lo que le falta',
    addGap: 'Agregar este dato',
    winsTitle: 'Lo que ya juega a tu favor',
    readFailed: 'No pudimos leer tu calificación. Intenta de nuevo.',
    labels: {
      strong: 'Lista para enviar',
      fair: 'Vas por buen camino',
      weak: 'Le falta material',
    },
    /**
     * La frase que empuja, por banda. Nombra CUÁNTOS datos faltan. La banda
     * fuerte (70 o más) dice «lista para enviar»: de ahí para arriba es pulir.
     */
    headline: {
      strongReady: 'Tu hoja de vida está lista para enviar.',
      strongGaps: (gaps: number) => `Tu hoja de vida está lista para enviar. Con ${datos(gaps)} más queda impecable.`,
      fairNoGaps: 'Vas bien. Agrega detalles concretos y sube de liga.',
      fairGaps: (gaps: number) => `Estás a ${datos(gaps)} de una hoja de vida fuerte.`,
      weakNoGaps: 'Cuéntanos más de lo que has hecho y tu hoja de vida cambia de liga.',
      weakGaps: (gaps: number) => `Estás a ${datos(gaps)} de que un empleador te tome en serio.`,
    },
    /** Los cuatro sub-puntajes, con nombre de persona. */
    subscores: {
      experience: { label: 'Experiencia', hint: 'Cuánto has trabajado y en qué' },
      skills: { label: 'Habilidades', hint: 'Máquinas, herramientas, certificados' },
      specificity: { label: 'Datos concretos', hint: 'Números, cantidades, cuánto tiempo' },
      completeness: { label: 'Qué tan completa', hint: 'Que no falten secciones' },
    },
  },

  /**
   * «Mejorar mi hoja de vida» (`JobApplyImproveCVPage`, `ProfilePieceCard`,
   * `api/profileClient.ts`) y el aviso del Panel tras la migración. Una tarjeta
   * por carencia con su casilla vacía, y debajo «Mis datos»: las piezas con sus
   * notas, para editar o borrar.
   */
  improveCV: {
    title: 'Mejorar mi hoja de vida',
    intro: 'Responde las que puedas y guarda todo junto al final. Cada respuesta se guarda en su lugar y no se borra.',
    /** Un solo botón para todas las tarjetas: el puntaje se recalcula una vez. */
    saveAll: (n: number) => (n === 0 ? 'Guardar respuestas' : n === 1 ? 'Guardar 1 respuesta' : `Guardar ${n} respuestas`),
    saveAllHint: 'Tu puntaje se recalcula una sola vez, cuando guardas.',
    howItWorks: 'Cómo se calcula tu puntaje',
    dismissed: 'Marcaste que no tienes esto. Se guarda con las demás respuestas.',
    undoDismiss: 'Deshacer',
    save: 'Guardar',
    dismiss: 'No tengo esto',
    saving: 'Estamos actualizando tu hoja de vida',
    up: (n: number) => `Subió ${puntos(n)}`,
    same: 'Se mantuvo: ya teníamos ese dato',
    down: (n: number, reason: string) => `Bajó ${puntos(n)}: ${reason}`,
    noGaps: 'No te falta nada importante. Si quieres, agrega otro trabajo o un curso.',
    myData: 'Mis datos',
    addJob: 'Agregar otro trabajo',
    addEducation: 'Agregar un estudio',
    addLicense: 'Agregar una licencia',
    edit: 'Editar',
    delete: 'Borrar',
    confirmDelete: '¿Seguro? Esto sí se borra.',
    recovered: 'Recuperado de una versión anterior',
    /** El Panel, los 14 días después de la migración (`cv_score_note: 'method_change'`). */
    methodChange:
      'Recalculamos tu puntaje con el método nuevo: ahora suma cada dato que das y no baja cuando agregas.',
    score: (n: number) => `Tu puntaje: ${n} de 100`,
    loading: 'Cargando tu hoja de vida',
    loadFailed: 'No pudimos cargar tu hoja de vida. Intenta de nuevo.',
    retry: 'Reintentar',
    saveFailed: 'No pudimos guardar. Intenta de nuevo.',
    /** 409 `profile_busy`: otra pestaña u otro teléfono guardaba al mismo tiempo. */
    busy: 'Tu hoja de vida se estaba guardando en otro lado. Intenta de nuevo.',
    gapGone: 'Esa pregunta ya no está en tu hoja de vida.',
    /** 422 `answer_is_question`: escribió una pregunta en la casilla («¿a qué trabajo te refieres?»). */
    answerIsQuestion:
      'Eso parece una pregunta. Arriba dice de qué trabajo o dato se trata: responde con el dato, o toca «No tengo esto».',
    /** La pregunta volvió después de responder la mitad: lo que ya dijo, para que no parezca repetida. */
    alreadySaid: (text: string) => `Ya nos dijiste: «${text}»`,
    checkFields: 'Revisa lo que escribiste.',
    /** 400 `no_change`: guardó la pieza sin cambiarle nada. */
    noChange: 'No cambiaste nada.',
    /** 409 `singleton_exists`: habilidades, idiomas y capacidad física son una sola sección. */
    singletonExists: 'Ya tienes esa sección: edítala en Mis datos en vez de agregar otra.',
    /**
     * La pregunta se cerró al guardar, pero en la casilla quedó algo escrito
     * después (mientras guardaba): no se pierde, se ofrece guardarlo aparte.
     */
    leftoverTitle: 'Esto lo escribiste después y todavía no se guardó:',
    leftoverSave: 'Guardar como dato adicional',
    leftoverDiscard: 'Descartar',
    noProfile: 'Todavía no tienes hoja de vida. Sube una o contesta el cuestionario.',
    toQuestionnaire: 'Contestar el cuestionario',
    back: 'Volver a mi hoja de vida',
    invitation: 'Cuéntanos en tus palabras. Puedes hablar o escribir.',
    preview: 'Así sale en tu hoja de vida',
    notes: 'Lo que nos contaste',
    deleteNote: 'Borrar esta nota',
    cancel: 'Cancelar',
    current: 'Actual',
    /** Destildó «Todavía trabajo aquí» sin decir cuándo terminó. */
    endYearNeeded: 'Si ya no trabajas aquí, dinos en qué año terminaste.',
    /** El nombre de cada tipo de pieza. */
    kinds: {
      job: 'Trabajo',
      education: 'Estudio',
      license: 'Licencia',
      certification: 'Curso o certificado',
      skills: 'Habilidades',
      languages: 'Idiomas',
      physical: 'Capacidad física',
      other: 'Otro dato',
    },
    /** Las etiquetas del formulario de agregar y editar. */
    fields: {
      company: 'Empresa o finca',
      role: 'Cargo',
      start_year: 'Año en que empezaste',
      end_year: 'Año en que terminaste',
      stillThere: 'Todavía trabajo aquí',
      level: 'Qué estudiaste',
      institution: 'Colegio o institución',
      city: 'Ciudad',
      year: 'Año',
      type: 'Tipo de licencia',
      country: 'País',
      since_year: 'Desde qué año la tienes',
      name: 'Nombre del curso o certificado',
      issuer: 'Quién lo dio',
      label: 'Qué es',
      items: 'Sepáralas con comas',
      text: 'En tus palabras',
    },
  },

  /** `JobApplyPreferencesPage` y `lib/preferences.ts`. */
  preferences: {
    loadFailed: 'No pudimos cargar tus preferencias. Recarga la página.',
    saveFailed: 'No se pudieron guardar tus preferencias.',
    saved: 'Listo, guardamos tus preferencias.',
    connectionError: 'Error de conexión. Intenta de nuevo.',
    loading: 'Cargando tus preferencias',
    rolesLabel: 'Cargos que buscas',
    rolesPlaceholder: 'Ej: Operario de cosecha, Ayudante de cocina',
    rolesHint: 'Sepáralos con comas.',
    locationsLabel: 'Dónde quieres trabajar',
    locationsPlaceholder: 'Ej: Florida, Texas, Remoto',
    jobTypesLabel: 'Tipo de empleo',
    industriesLabel: 'Sectores que te interesan',
    industriesPlaceholder: 'Ej: Agricultura, Hotelería, Construcción',
    salaryLabel: 'Salario que esperas',
    salaryMinPlaceholder: 'Desde',
    salaryMaxPlaceholder: 'Hasta (opcional)',
    currencyPlaceholder: 'USD',
    salaryHint: 'Si dejas el "hasta" vacío, entendemos que no tienes tope. Déjalo todo en blanco si prefieres no decirlo.',
    excludedLabel: 'Empresas que no quieres ver',
    excludedPlaceholder: 'Ej: tu empleador actual',
    excludedHint: 'No te sugerimos a estas empresas ni les apareces en sus búsquedas.',
    submit: 'Guardar preferencias',
    /** Los países NO se eligen acá: mueven el envío, y viven en el panel. */
    countriesPointer: 'Los países donde postulamos por ti se eligen en tu Panel.',
    countriesPointerLink: 'Ir al Panel',
    salaryRangeInverted: 'El salario máximo no puede ser menor que el mínimo.',
    salaryPeriods: {
      hour: 'por hora',
      day: 'por día',
      week: 'por semana',
      month: 'por mes',
      year: 'por año',
    },
    jobTypes: {
      full_time: 'Tiempo completo',
      part_time: 'Medio tiempo',
      temporary: 'Temporal',
      seasonal: 'Por temporada',
      contract: 'Por contrato',
    },
  },

  /** `JobApplyTalentPoolPage`: el consentimiento. */
  talentPool: {
    loadFailed: 'No pudimos leer tu decisión. Recarga la página.',
    saveFailed: 'No pudimos guardar tu decisión.',
    joined: 'Ya apareces en la bolsa de talento. Puedes salir cuando quieras.',
    left: 'Saliste de la bolsa de talento. No apareces en nuevas búsquedas.',
    connectionError: 'Error de conexión. Intenta de nuevo.',
    loading: 'Cargando tu decisión',
    title: 'Quién te encuentra',
    /** Con el interruptor de free tier apagado, el título de antes. */
    titleLegacy: 'Que las agencias te encuentren',
    on: 'Activa',
    off: 'Apagada',
    lede: 'Las agencias que publican vacantes con nosotros pueden buscarte por tu experiencia, sin que tengas que postularte una por una.',
    toggleLabel: 'Aparecer en la bolsa de talento',
    profileTitle: 'Perfil público',
    profileLede: 'Tu perfil tiene su propia página en TBit Jobs y puede aparecer en Google.',
    profileView: 'Ver mi perfil público',
    profileCopy: 'Copiar enlace',
    profileCopied: 'Enlace copiado',
    profileToggleLabel: 'Perfil público',
    profileMadePublic: 'Tu perfil es público.',
    profileMadePrivate: 'Tu perfil es privado. Tu página deja de mostrarse en unos minutos.',
    profileSave: 'Hacer público mi perfil',
    profileCancel: 'Cancelar',
    seenTitle: 'Lo que ven',
    seenBody: 'Tu nombre y apellido, tu experiencia y tus habilidades: un resumen de tu hoja de vida, sin tus datos personales.',
    neverSeenTitle: 'Lo que nunca ven',
    neverSeenBody: 'Tu correo y tu teléfono. Si una agencia quiere hablar contigo, te llega una invitación y tú decides si aceptas. Solo ahí reciben tus datos.',
    footerOn: 'Puedes salir cuando quieras: dejas de aparecer en nuevas búsquedas de inmediato.',
    footerOff: 'Estás fuera de la bolsa. Tu hoja de vida solo se usa para las postulaciones que hacemos por ti; ninguna agencia puede encontrarte por su cuenta.',
  },

  /**
   * «Tu situación migratoria» (H-2): el paso del alta después de la hoja de
   * vida y su tarjeta en Perfil → Preferencias. Frases cortas y sin siglas de
   * más: la lee gente con poca escolaridad, a veces desde un teléfono prestado.
   */
  immigration: {
    step: 'Una pregunta más',
    title: 'Tu situación migratoria',
    lede: 'Así le contamos bien tu caso a cada empresa de Estados Unidos. Elige la opción que se parece a ti.',
    groupLabel: 'Tu situación migratoria',
    options: {
      first_time: { label: 'Primera vez', hint: 'Nunca has tenido visa H-2A ni H-2B.' },
      returner: {
        label: 'Returner',
        hint: 'Ya trabajaste con H-2A o H-2B en temporadas anteriores y hoy estás fuera de Estados Unidos.',
      },
      extension: {
        label: 'Extensión',
        hint: 'Estás en Estados Unidos con H-2 y tu empleador actual va a extender tu estadía.',
      },
      transfer: {
        label: 'Transferencia',
        hint: 'Estás en Estados Unidos con H-2 y quieres pasar a un empleador nuevo.',
      },
    },
    employerLabel: '¿Con qué empleador y en qué temporada?',
    employerOptional: ' (opcional)',
    employerPlaceholder: 'Ej: Sunny Farms, temporada 2025',
    employerHint: 'Lo ponemos en tus correos para que la empresa vea que ya conoces el trabajo con visa.',
    note: 'Esto no cambia a qué vacantes postulamos: solo cómo te presentamos.',
    submit: 'Guardar y seguir',
    save: 'Guardar',
    saved: 'Listo, guardamos tu situación migratoria.',
    pickOne: 'Elige una opción para seguir.',
    saveFailed: 'No pudimos guardar tu respuesta. Intenta de nuevo.',
    connectionError: 'Error de conexión. Intenta de nuevo.',
    cardLede: 'Cómo te presentamos ante las empresas de Estados Unidos. Puedes cambiarla cuando quieras.',
    /** La pregunta de papeles de la UE: a quien eligió Europa (EURES). */
    eu: {
      lede: 'Así sabemos a qué vacantes de Europa postularte. Elige la opción que se parece a ti.',
      question: '¿Puedes trabajar legalmente en la Unión Europea?',
      groupLabel: '¿Puedes trabajar legalmente en la Unión Europea?',
      options: {
        citizen: { label: 'Sí, tengo pasaporte de un país de la UE' },
        resident: { label: 'Sí, tengo residencia o permiso de trabajo vigente en un país de la UE' },
        none: { label: 'No' },
      },
      noneHint:
        'Te postulamos a vacantes que dicen aceptar candidatos de fuera de la UE o que ayudan con la visa, y a oficios con permiso por escasez en España: electricistas, carpintería de aluminio, metálica o PVC, operadores de grúa y marina mercante.',
      note: 'Esto cambia a qué vacantes de Europa postulamos y lo que decimos en tus correos.',
      pickOne: 'Dinos si puedes trabajar en la Unión Europea para seguir.',
      cardTitle: 'Trabajar en la Unión Europea',
      cardLede: 'Con papeles de la UE te postulamos a todas las vacantes de Europa; sin papeles, a las que aceptan candidatos de fuera y a los oficios con permiso por escasez en España. Puedes cambiarlo cuando quieras.',
      saved: 'Listo, guardamos tu respuesta.',
    },
  },
};

export default onboarding;
